import React, { useState } from 'react'; // Added useState
import { Link } from 'react-router-dom';
import { Heart, Star, ImageOff } from 'lucide-react'; // Added ImageOff icon
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

export const ProductCard = ({ product }) => {
  const { isInWishlist, toggleWishlist } = useCart();
  const { user } = useAuth();
  const [imgError, setImgError] = useState(false); // Handle broken links

  if (!product) return null;

  const inWishlist = isInWishlist(product.id);
  const price = product.price || 0;
  const discount = product.discount || 0;
  const discountedPrice = price * (1 - discount / 100);

  // LOGIC: Use first image, or null if empty
  const hasImage = product.images && product.images.length > 0;
  const mainImage = hasImage ? product.images[0] : null;

  const handleWishlistClick = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to add to wishlist');
      return;
    }
    try {
      await toggleWishlist(product);
      toast.success(inWishlist ? 'Removed from wishlist' : 'Added to wishlist');
    } catch (error) {
      toast.error('Failed to update wishlist');
    }
  };

  return (
    <div className='group relative bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 p-3'>
      <Link
        to={`/product/${product.id}`}
        className="group block"
      >
        <div className="relative overflow-hidden rounded-xl bg-gray-100">
          <div className="aspect-[3/4] overflow-hidden flex items-center justify-center">

            {/* IMAGE LOGIC */}
            {!imgError && mainImage ? (
              <img
                src={mainImage}
                alt={product.name}
                onError={() => setImgError(true)} // If URL fails, switch to fallback
                className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              // FALLBACK PLACEHOLDER
              <div className="flex flex-col items-center justify-center text-gray-300">
                <ImageOff className="w-10 h-10 mb-2" />
                <span className="text-xs">No Image</span>
              </div>
            )}

          </div>

          <button
            onClick={handleWishlistClick}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-yellow-50 transition-colors z-10"
          >
            <Heart className={`w-5 h-5 ${inWishlist ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} />
          </button>

          <div className="absolute top-3 left-3 flex flex-col gap-2">
            {discount > 0 && (
              <span className="bg-yellow-400 text-black px-2 py-1 rounded text-xs font-bold">
                {discount}% OFF
              </span>
            )}
          </div>
        </div>

        <div className="mt-3 space-y-1">
          <h3 className="font-medium h-10 text-sm line-clamp-2 text-gray-800 group-hover:text-black">{product.name}</h3>

          <div className="flex items-center gap-2">
            <span className="font-bold text-lg">
              ₹{discountedPrice.toFixed(0)}
            </span>
            {discount > 0 && (
              <span className="text-gray-400 text-sm line-through">
                ₹{price.toFixed(0)}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1">
            <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
            <span className="text-xs text-gray-500">{product.rating || 4.5}</span>
          </div>
        </div>
      </Link>
    </div>
  );
};